import React, { useEffect, useState, useCallback } from "react";
import {
  Form,
  Button,
  Row,
  Col,
  Table,
  Card,
  Modal,
  InputGroup,
  FormControl,
  Spinner,
} from "react-bootstrap";
import ReactPaginate from "react-paginate";
import api from "../../axiosConfig";
import PartPickerModal from "../pickers/PartPickerModal";

export default function CatalogForm({ initialData, onSubmit, submitting }) {
  const [form, setForm] = useState({
    nume_catalog: "",
  });

  const isEdit = !!initialData?.id;

  // Pret Catalog details (sub-table)
  const [prices, setPrices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [priceSearch, setPriceSearch] = useState("");

  // Options for valuta
  const [valutaOptions, setValutaOptions] = useState([]);

  // Modal to add product price
  const [showAddModal, setShowAddModal] = useState(false);
  const [partPickerShow, setPartPickerShow] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null); // { id, cod, denumire, um }
  const [addForm, setAddForm] = useState({
    pret: "",
    valuta_id: "",
  });
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);

  // Load valute and details
  useEffect(() => {
    if (initialData) {
      setForm({
        nume_catalog: initialData.nume_catalog ?? "",
      });
    }
  }, [initialData]);

  useEffect(() => {
    api.get("/bxvaluta/", { params: { limit: 50 } })
      .then((res) => {
        const items = res.data?.items ?? [];
        setValutaOptions(items);
        if (items.length > 0) {
          setAddForm((prev) => ({ ...prev, valuta_id: items[0].id }));
        }
      })
      .catch((err) => console.error("Error loading valuta options:", err));
  }, []);

  const fetchPrices = useCallback(async () => {
    if (!isEdit) return;
    setLoadingPrices(true);
    const skip = currentPage * itemsPerPage;
    try {
      const res = await api.get("/pret-catalog", {
        params: {
          catalog_id: initialData.id,
          search: priceSearch,
          skip,
          limit: itemsPerPage,
        },
      });
      setPrices(res.data?.items ?? []);
      setTotalCount(res.data?.total ?? 0);
    } catch (err) {
      console.error("Error loading prices:", err);
    } finally {
      setLoadingPrices(false);
    }
  }, [isEdit, initialData?.id, priceSearch, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nume_catalog.trim()) {
      alert("Denumirea catalogului este obligatorie.");
      return;
    }
    onSubmit(form);
  };

  // Delete price item
  const handleDeletePrice = async (priceId) => {
    if (window.confirm("Sigur dorești să ștergi acest preț din catalog?")) {
      try {
        await api.delete(`/pret-catalog/${priceId}`);
        fetchPrices();
      } catch (err) {
        alert("Eroare la ștergere preț.");
      }
    }
  };

  // Add price item
  const handleAddPriceSubmit = async (e) => {
    e.preventDefault();
    setAddError("");
    if (!selectedPart) {
      setAddError("Te rugăm să alegi un articol.");
      return;
    }
    if (!addForm.pret || isNaN(Number(addForm.pret)) || Number(addForm.pret) <= 0) {
      setAddError("Prețul trebuie să fie un număr valid mai mare decât 0.");
      return;
    }
    if (!addForm.valuta_id) {
      setAddError("Te rugăm să alegi valuta.");
      return;
    }

    setAdding(true);
    try {
      await api.post("/pret-catalog/", {
        catalog_id: initialData.id,
        bxpart_id: selectedPart.id,
        pret: Number(addForm.pret),
        valuta_id: Number(addForm.valuta_id),
      });
      setShowAddModal(false);
      setSelectedPart(null);
      setAddForm((prev) => ({ ...prev, pret: "" }));
      fetchPrices();
    } catch (err) {
      const msg = err.response?.data?.detail || "Eroare la adăugare.";
      setAddError(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleEditPriceInline = async (priceItem, newPret) => {
    if (isNaN(Number(newPret)) || Number(newPret) <= 0) return;
    try {
      await api.put(`/pret-catalog/${priceItem.id}`, {
        pret: Number(newPret),
      });
      fetchPrices();
    } catch (err) {
      alert("Eroare la actualizarea prețului: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleEditValutaInline = async (priceItem, newValutaId) => {
    try {
      await api.put(`/pret-catalog/${priceItem.id}`, {
        valuta_id: Number(newValutaId),
      });
      fetchPrices();
    } catch (err) {
      alert("Eroare la actualizarea valutei: " + (err.response?.data?.detail || err.message));
    }
  };

  const pageCount = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  return (
    <div className="catalog-form-container">
      <Card className="shadow-sm border-0 p-4 bg-white rounded-3 mb-4">
        <Form onSubmit={handleSubmit}>
          <Row className="align-items-end g-3">
            <Col xs={12} md={8}>
              <Form.Group controlId="nume_catalog">
                <Form.Label className="fw-semibold text-secondary">Denumire Catalog</Form.Label>
                <Form.Control
                  name="nume_catalog"
                  value={form.nume_catalog}
                  onChange={handleChange}
                  placeholder="Ex: Catalog Clienti Distributie"
                  className="py-2 border-secondary-subtle"
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4} className="text-end">
              <Button type="submit" variant="dark" className="px-4 py-2 fw-semibold rounded-2 w-100 w-md-auto" disabled={submitting}>
                {submitting ? "Se salvează..." : "Salvează Catalog"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      {isEdit && (
        <Card className="shadow-sm border-0 p-4 bg-white rounded-3">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
            <div>
              <h4 className="text-dark fw-bold m-0">Articole & Prețuri în Catalog</h4>
              <p className="text-muted small m-0">Administrează prețurile de vânzare pentru acest catalog</p>
            </div>
            <Button variant="outline-dark" className="fw-semibold px-4 py-2" onClick={() => setShowAddModal(true)}>
              + Adaugă Articol
            </Button>
          </div>

          <Row className="mb-3 g-2">
            <Col xs={12}>
              <InputGroup>
                <FormControl
                  placeholder="Caută articol după cod sau denumire în catalog..."
                  value={priceSearch}
                  onChange={(e) => {
                    setPriceSearch(e.target.value);
                    setCurrentPage(0);
                  }}
                  className="py-2 border-secondary-subtle"
                />
                {priceSearch && (
                  <Button variant="outline-secondary" onClick={() => setPriceSearch("")}>
                    Curăță
                  </Button>
                )}
              </InputGroup>
            </Col>
          </Row>

          {loadingPrices ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="dark" />
              <p className="text-muted mt-2 small">Se încarcă lista de prețuri...</p>
            </div>
          ) : (
            <>
              <Table responsive hover className="align-middle border-light-subtle mb-4">
                <thead className="table-light text-secondary">
                  <tr>
                    <th>Cod</th>
                    <th>Denumire</th>
                    <th>UM</th>
                    <th style={{ width: 150 }}>Preț Catalog</th>
                    <th style={{ width: 120 }}>Valută</th>
                    <th style={{ width: 100 }} className="text-end">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {prices.map((p) => (
                    <tr key={p.id}>
                      <td className="fw-semibold text-secondary">{p.cod || "-"}</td>
                      <td className="fw-semibold text-dark">{p.denumire || "-"}</td>
                      <td>{p.um || "-"}</td>
                      <td>
                        <input
                          type="number"
                          step="0.0001"
                          defaultValue={p.pret}
                          onBlur={(e) => handleEditPriceInline(p, e.target.value)}
                          className="form-control form-control-sm text-end border-secondary-subtle"
                          style={{ maxWidth: "120px" }}
                        />
                      </td>
                      <td>
                        <select
                          value={p.valuta_id}
                          onChange={(e) => handleEditValutaInline(p, e.target.value)}
                          className="form-select form-select-sm border-secondary-subtle"
                        >
                          {valutaOptions.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.cod}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="text-end">
                        <Button variant="outline-danger" size="sm" onClick={() => handleDeletePrice(p.id)}>
                          Șterge
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {prices.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-4 text-muted">
                        Nu există niciun preț definit în catalog.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              <ReactPaginate
                previousLabel={"← Înapoi"}
                nextLabel={"Înainte →"}
                pageCount={pageCount}
                forcePage={currentPage}
                onPageChange={(event) => setCurrentPage(event.selected)}
                containerClassName={"pagination justify-content-center"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link text-dark"}
                previousClassName={"page-item"}
                previousLinkClassName={"page-link text-dark"}
                nextClassName={"page-item"}
                nextLinkClassName={"page-link text-dark"}
                activeClassName={"active"}
              />
            </>
          )}
        </Card>
      )}

      {/* Modal: Adaugă preț articol */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Adaugă articol în catalog</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addError && <div className="alert alert-danger py-2 small">{addError}</div>}
          <Form onSubmit={handleAddPriceSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">Articol</Form.Label>
              <div className="d-flex gap-2">
                <FormControl
                  readOnly
                  placeholder="Click pentru a alege un articol..."
                  value={selectedPart ? `${selectedPart.cod} - ${selectedPart.denumire}` : ""}
                  onClick={() => setPartPickerShow(true)}
                  className="bg-light cursor-pointer"
                />
                <Button variant="outline-dark" onClick={() => setPartPickerShow(true)}>
                  Alege
                </Button>
              </div>
            </Form.Group>

            <Row className="mb-3">
              <Col xs={6}>
                <Form.Group controlId="add_pret">
                  <Form.Label className="fw-semibold text-secondary">Preț de vânzare</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.0001"
                    name="pret"
                    value={addForm.pret}
                    onChange={(e) => setAddForm((f) => ({ ...f, pret: e.target.value }))}
                    placeholder="0.00"
                    required
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group controlId="add_valuta">
                  <Form.Label className="fw-semibold text-secondary">Valută</Form.Label>
                  <Form.Select
                    name="valuta_id"
                    value={addForm.valuta_id}
                    onChange={(e) => setAddForm((f) => ({ ...f, valuta_id: e.target.value }))}
                    required
                  >
                    {valutaOptions.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.cod}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="outline-secondary" onClick={() => setShowAddModal(false)} disabled={adding}>
                Renunță
              </Button>
              <Button type="submit" variant="dark" disabled={adding}>
                {adding ? "Se adaugă..." : "Adaugă Articol"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Picker: Articol */}
      <PartPickerModal
        show={partPickerShow}
        onHide={() => setPartPickerShow(false)}
        minChars={0}
        filter={{}} // Fără filtre, permite toate
        onPick={(opt) => {
          setSelectedPart({
            id: opt.id,
            cod: opt.raw?.cod || opt.code || "",
            denumire: opt.raw?.denumire || opt.label || "",
            um: opt.raw?.um?.cod || "",
          });
          const p = opt.raw?.pret_vanzare || opt.raw?.price_sale || opt.raw?.price || opt.raw?.pret || "";
          if (p) {
            setAddForm((prev) => ({ ...prev, pret: String(p) }));
          }
        }}
      />
    </div>
  );
}
